import PageLoader from "@/components/loader/PageLoader";
import { useAxiosInstance } from "@/utils/useAxiosInstance";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

interface EditDeviceProps {
  onMenuItemClick: (componentName: string, successMessage?: string) => void;
  setSuccessMessage: (message: string) => void;
  deviceId: number | null;
}

interface Device {
  id: number;
  device_type: string;
  name: string;
  branch: number;
  branch_name: string;
  is_active: boolean;
  last_connected: string | null;
  connection_status: string;
  device_identifier: string;
  os_version: string | null;
  app_version: string | null;
  device_model: string;
  is_connected: boolean;
  created: string;
  modified: string;
  linked_terminal: number | null;
  linked_terminal_details: any | null;
  linked_printers: number[];
  linked_printers_details: any | null;
  receipt_copies: number;
}

const EditDevice = ({
  onMenuItemClick,
  setSuccessMessage,
  deviceId,
}: EditDeviceProps) => {
  const { t } = useTranslation();
  const [axiosInstance, loading, isFetching] = useAxiosInstance();
  const [device, setDevice] = useState<Device | null>(null);

  useEffect(() => {
    const fetchDevice = async () => {
      try {
        if (!loading && axiosInstance && deviceId) {
          const apiUrl = `${import.meta.env.VITE_APP_API_BASE_URL}device/`;
          const response = await axiosInstance.get(`${apiUrl}${deviceId}/`);
          setDevice(response.data);
        }
      } catch (error) {
        console.error("Error fetching device:", error);
      }
    };

    if (deviceId) {
      fetchDevice();
    }
  }, [axiosInstance, loading, deviceId]);

  const handleUpdateDevice = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    try {
      if (!loading && axiosInstance && device) {
        const apiUrl = `${import.meta.env.VITE_APP_API_BASE_URL}device/`;

        // Only send the receipt_copies field for update
        const response = await axiosInstance.patch(
          `${apiUrl}${deviceId}/`,
          { receipt_copies: device.receipt_copies },
          {
            headers: {
              "Content-Type": "application/json",
            },
          },
        );

        if (response.status === 200) {
          const successMessage = t(
            "back.management.business.device.successUpdate"
          );
          
          // Show success message but stay on the same page
          setSuccessMessage(successMessage);
          toast.success(successMessage);
        }
      }
    } catch (error) {
      console.error("Error updating device", error);
    }
  };

  const handleReceiptCopiesChange = (value: string) => {
    if (device) {
      setDevice({
        ...device,
        receipt_copies: parseInt(value),
      });
    }
  };

  return (
    <PageLoader isFetching={isFetching}>
      <div className="container mx-auto px-4 py-2">
        {device ? (
          <>
            <h1 className="text-xl font-bold">
              {t("back.management.business.device.editDevice")}
            </h1>
            <div className="mt-4 rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <div>
                  <h2 className="text-lg font-medium text-gray-700">
                    {t("back.management.business.device.deviceInfo")}
                  </h2>
                  <div className="mt-4 space-y-4">
                    <div>
                      <p className="text-sm font-medium text-gray-500">
                        {t("back.management.business.device.name")}
                      </p>
                      <p className="text-gray-900">{device.name}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500">
                        {t("back.management.business.device.deviceIdentifier")}
                      </p>
                      <p className="text-gray-900">{device.device_identifier}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500">
                        {t("back.management.business.device.deviceType")}
                      </p>
                      <p className="text-gray-900">{device.device_type}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500">
                        {t("back.management.business.device.deviceModel")}
                      </p>
                      <p className="text-gray-900">{device.device_model}</p>
                    </div>
                  </div>
                </div>
                <div>
                  <h2 className="text-lg font-medium text-gray-700">
                    {t("back.management.business.device.connectionInfo")}
                  </h2>
                  <div className="mt-4 space-y-4">
                    <div>
                      <p className="text-sm font-medium text-gray-500">
                        {t("back.management.business.device.connectionStatus")}
                      </p>
                      <p className="text-gray-900">{device.connection_status}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500">
                        {t("back.management.business.device.branch")}
                      </p>
                      <p className="text-gray-900">{device.branch_name}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500">
                        {t("back.management.business.device.lastConnected")}
                      </p>
                      <p className="text-gray-900">
                        {device.last_connected || "Never"}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <form className="mt-8 space-y-6" onSubmit={handleUpdateDevice}>
              <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
                <h2 className="mb-4 text-lg font-medium text-gray-700">
                  {t("back.management.business.device.settings")}
                </h2>
                <div className="space-y-4">
                  <div>
                    <label
                      htmlFor="receipt_copies"
                      className="mb-2 block text-sm font-medium text-gray-700"
                    >
                      {t("back.management.business.device.receiptCopies")}
                    </label>
                    <Select
                      value={device.receipt_copies.toString()}
                      onValueChange={handleReceiptCopiesChange}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select number of receipt copies" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="0">0</SelectItem>
                        <SelectItem value="1">1</SelectItem>
                        <SelectItem value="2">2</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-4">
                <button
                  type="button"
                  onClick={() => onMenuItemClick("devices")}
                  className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                >
                  {t("back.management.menu.product.cancelbutton")}
                </button>
                <button
                  type="submit"
                  className="rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                >
                  {t("back.management.business.device.updateDevice")}
                </button>
              </div>
            </form>
          </>
        ) : (
          <p>{t("back.management.business.device.noDeviceSelected")}</p>
        )}
      </div>
    </PageLoader>
  );
};

export default EditDevice;
